import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TrendingDown, Plus, Loader2, Scale, Pencil, Trash2, Check, X, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip } from "recharts";

interface ProgressLog {
  id: string;
  value: number;
  unit: string;
  logged_at: string;
}

interface ProgressTrackerProps {
  goalIncludesWeight: boolean;
}

const ProgressTracker = ({ goalIncludesWeight }: ProgressTrackerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [logs, setLogs] = useState<ProgressLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const fetchLogs = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("patient_progress_logs")
      .select("id, value, unit, logged_at")
      .eq("user_id", user.id)
      .eq("metric_type", "weight")
      .order("logged_at", { ascending: true })
      .limit(50);
    setLogs(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, [user]);

  if (!goalIncludesWeight) return null;

  const handleSubmit = async () => {
    if (!user || !newValue) return;
    const val = parseFloat(newValue);
    if (isNaN(val) || val <= 0) {
      toast({ title: "Invalid weight", description: "Please enter a valid number.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("patient_progress_logs").insert({
      user_id: user.id,
      metric_type: "weight",
      value: val,
      unit: "lbs",
    });
    if (error) {
      toast({ title: "Error", description: "Could not save entry.", variant: "destructive" });
    } else {
      toast({ title: "Logged!", description: `Weight: ${val} lbs recorded.` });
      setNewValue("");
      setShowInput(false);
      fetchLogs();
    }
    setSubmitting(false);
  };

  const handleEdit = async (id: string) => {
    const val = parseFloat(editValue);
    if (isNaN(val) || val <= 0) {
      toast({ title: "Invalid weight", description: "Please enter a valid number.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase
      .from("patient_progress_logs")
      .update({ value: val })
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: "Could not update entry.", variant: "destructive" });
    } else {
      toast({ title: "Updated", description: `Weight updated to ${val} lbs.` });
      setEditingId(null);
      setEditValue("");
      fetchLogs();
    }
    setSubmitting(false);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase
      .from("patient_progress_logs")
      .delete()
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: "Could not delete entry.", variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "Weight entry removed." });
      fetchLogs();
    }
    setDeletingId(null);
  };

  // Check if logged this week
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const loggedThisWeek = logs.some((l) => new Date(l.logged_at) >= oneWeekAgo);

  const chartData = logs.map((l) => ({
    date: new Date(l.logged_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    weight: Number(l.value),
  }));

  const latestWeight = logs.length > 0 ? Number(logs[logs.length - 1].value) : null;
  const startWeight = logs.length > 1 ? Number(logs[0].value) : null;
  const totalChange = latestWeight && startWeight ? latestWeight - startWeight : null;

  // Show most recent entries first in the history list
  const recentLogs = [...logs].reverse();

  if (loading) return null;

  // Friendly empty state — no entries yet
  if (logs.length === 0) {
    return (
      <div className="mb-10">
        <h2 className="font-display text-lg text-foreground mb-4">Progress Tracker</h2>
        <Card className="border-primary/15 bg-gradient-to-br from-primary/5 via-card to-secondary/30 overflow-hidden">
          <CardContent className="p-10 text-center">
            <div className="relative mx-auto mb-5 w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-primary/10 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center ring-4 ring-primary/5">
                <Scale size={30} className="text-primary" strokeWidth={1.75} />
              </div>
              <Sparkles size={14} className="absolute -top-1 -right-1 text-primary/60" />
            </div>
            <h3 className="font-display text-xl text-foreground mb-2">Start tracking your progress</h3>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto leading-relaxed">
              Log your weight weekly to visualize your journey and share progress with your provider.
            </p>
            {showInput ? (
              <div className="flex gap-2 justify-center max-w-xs mx-auto">
                <Input
                  type="number"
                  placeholder="Weight in lbs"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="max-w-[160px]"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                </Button>
              </div>
            ) : (
              <Button size="lg" onClick={() => setShowInput(true)} className="gap-2 shadow-sm">
                <Plus size={16} /> Log your first entry
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <h2 className="font-display text-lg text-foreground mb-4">Progress Tracker</h2>

      {/* Weekly prompt */}
      {!loggedThisWeek && (
        <Card className="border-amber-300/50 bg-amber-50/50 mb-4">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Scale size={18} className="text-amber-600" />
              <p className="text-sm font-medium text-amber-800">Time to log your weight this week!</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowInput(true)} className="text-xs">
              Log Now
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-border bg-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingDown size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Weight</p>
                {latestWeight && (
                  <p className="text-xs text-muted-foreground">
                    Current: {latestWeight} lbs
                    {totalChange !== null && (
                      <span className={totalChange < 0 ? " text-green-600" : totalChange > 0 ? " text-red-500" : ""}>
                        {" "}({totalChange > 0 ? "+" : ""}{totalChange.toFixed(1)} lbs)
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowInput(!showInput)} className="gap-1 text-xs">
              <Plus size={14} /> Log
            </Button>
          </div>

          {showInput && (
            <div className="flex gap-2 mb-4">
              <Input
                type="number"
                placeholder="Weight in lbs"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="max-w-[140px]"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
              <Button size="sm" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <Loader2 size={14} className="animate-spin" /> : "Save"}
              </Button>
            </div>
          )}

          {chartData.length >= 2 ? (
            <div className="h-[140px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis domain={["dataMin - 5", "dataMax + 5"]} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={40} />
                  <RechartsTooltip contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-6">
              {logs.length === 0 ? "Log your first weight entry to start tracking." : "Log at least 2 entries to see your chart."}
            </p>
          )}

          {/* History toggle */}
          {logs.length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {showHistory ? "Hide history" : `View all entries (${logs.length})`}
              </button>

              {showHistory && (
                <div className="mt-3 max-h-[220px] overflow-y-auto space-y-1">
                  {recentLogs.map((log) => {
                    const isEditing = editingId === log.id;
                    const isDeleting = deletingId === log.id;
                    const dateStr = new Date(log.logged_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    });

                    return (
                      <div
                        key={log.id}
                        className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 group transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{dateStr}</span>
                          {isEditing ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                type="number"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="h-7 w-20 text-xs"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleEdit(log.id);
                                  if (e.key === "Escape") setEditingId(null);
                                }}
                              />
                              <span className="text-xs text-muted-foreground">lbs</span>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => handleEdit(log.id)}
                                disabled={submitting}
                              >
                                <Check size={12} className="text-green-600" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => setEditingId(null)}
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-sm font-medium text-foreground">{Number(log.value)} lbs</span>
                          )}
                        </div>

                        {!isEditing && (
                          <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => {
                                setEditingId(log.id);
                                setEditValue(String(Number(log.value)));
                              }}
                            >
                              <Pencil size={13} className="text-muted-foreground" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => setConfirmDeleteId(log.id)}
                              disabled={isDeleting}
                            >
                              {isDeleting ? (
                                <Loader2 size={13} className="animate-spin" />
                              ) : (
                                <Trash2 size={13} className="text-destructive" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete weight entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this weight log. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteId) handleDelete(confirmDeleteId);
                setConfirmDeleteId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProgressTracker;
